import type { ChatMessage, MessageBlock, ReportProgressData, SuggestedFollowUp, TextBlock } from "@/lib/chat-types";
import type { WsIncomingEvent } from "@/lib/fund-compliance/ws-events";
import { resolveFundComplianceGatewayBaseUrl } from "@/lib/fund-compliance/gateway";
import { readString, toRecord, type JsonRecord } from "@/lib/fund-compliance/parse-primitives";
import { extractReviewBlocks } from "@/lib/fund-compliance/parse-findings";
import { extractCitationBlocks } from "@/lib/fund-compliance/parse-citations";
import { extractSuggestedFollowUps } from "@/lib/fund-compliance/parse-metadata";
import { stripRedundantReviewSections } from "@/lib/fund-compliance/strip-review-sections";
import {
  createAssistantTextMessage,
  createMessageId,
  extractEventText,
  mergeMessagesById,
  normalizeHistoryMessages,
  toText,
} from "@/lib/fund-compliance/messages";

/* ── Assistant message builder ───────────────────────────────────── */

/**
 * Accumulates all events within a single assistant turn (from user_message
 * echo → result) into ONE message. Each event mutates the builder and then
 * triggers an upsert of the corresponding ChatMessage.
 */
export interface AssistantBuilderState {
  messageId: string | null;
  streamId: string | null;
  chatId: string | null;

  // Accumulated data across events
  progressData: ReportProgressData | null;
  deltaText: string;
  reviewData: JsonRecord | null;
  citations: unknown[] | null;
  followUps: SuggestedFollowUp[] | null;

  showStatus: boolean;
}

export function createBuilder(): AssistantBuilderState {
  return {
    messageId: null,
    streamId: null,
    chatId: null,
    progressData: null,
    deltaText: "",
    reviewData: null,
    citations: null,
    followUps: null,
    showStatus: false,
  };
}

function resetBuilder(builder: AssistantBuilderState): void {
  Object.assign(builder, createBuilder());
}

function buildBlocks(builder: AssistantBuilderState): MessageBlock[] {
  const blocks: MessageBlock[] = [];

  if (builder.progressData) {
    blocks.push({ type: "report-progress", reportProgress: builder.progressData });
  }

  if (builder.deltaText) {
    // Once structured review data arrives, drop any narrative sections that
    // duplicate what FindingsGrid / BulletsBlock will render. During pure
    // streaming (reviewData still null) this is a no-op.
    const cleanedText = stripRedundantReviewSections(builder.deltaText, builder.reviewData);
    if (cleanedText) {
      blocks.push({ type: "text", content: cleanedText } satisfies TextBlock);
    }
  }

  if (builder.reviewData) {
    blocks.push(...extractReviewBlocks(builder.reviewData));
  }

  if (builder.citations) {
    blocks.push(...extractCitationBlocks(builder.citations));
  }

  return blocks;
}

function upsertAssistantMessage(
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  builder: AssistantBuilderState,
  chatId: string
): void {
  if (!builder.messageId) {
    builder.messageId = createMessageId("assistant");
    builder.chatId = chatId;
  }

  const messageId = builder.messageId;
  const blocks = buildBlocks(builder);
  if (blocks.length === 0) return;

  const followUps = builder.followUps ?? undefined;
  const showStatus = builder.showStatus;

  setMessages((prev) => {
    const existing = prev.findIndex((m) => m.id === messageId);
    if (existing === -1) {
      const newMessage: ChatMessage = {
        id: messageId,
        role: "assistant",
        blocks,
        suggestedFollowUps: followUps,
        showStatus,
        timestamp: Date.now(),
      };
      return [...prev, newMessage];
    }
    return prev.map((m, i) => {
      if (i !== existing) return m;
      const updated: ChatMessage = {
        ...m,
        blocks,
        suggestedFollowUps: followUps,
        showStatus,
      };
      return updated;
    });
  });
}

/* ── Progress merging ────────────────────────────────────────────── */

interface ProgressUpdate {
  pct?: number;
  step?: string;
  message?: string;
  report?: JsonRecord | null;
}

function mergeProgressData(
  current: ReportProgressData | null,
  update: ProgressUpdate
): ReportProgressData {
  const base: ReportProgressData = current ?? {
    title: "Compliance Review Progress",
    steps: [],
  };

  let nextReport = base.report;
  if (update.report) {
    const r = update.report;
    const statusRaw = readString(r.status)?.toLowerCase();
    const isReady = statusRaw === "ready" || statusRaw === "completed";
    const storageName = readString(r.storageName) ?? readString(r.storageKey) ?? readString(r.blobName) ?? readString(r.reportName);
    nextReport = {
      filename: readString(r.filename) ?? base.report?.filename ?? "Compliance report.pdf",
      filesize: readString(r.filesize) ?? readString(r.sizeLabel) ?? base.report?.filesize ?? "Preparing",
      status: isReady ? "ready" : base.report?.status ?? "generating",
      downloadUrl:
        readString(r.downloadUrl) ??
        readString(r.url) ??
        readString(r.href) ??
        (storageName ? `${resolveFundComplianceGatewayBaseUrl()}/api/storage/v1/files/${encodeURIComponent(storageName)}` : undefined) ??
        base.report?.downloadUrl,
      storageName: storageName ?? base.report?.storageName,
    };
  }

  return {
    ...base,
    title: update.message ?? base.title,
    pct: update.pct ?? base.pct,
    pctMessage: update.message ?? update.step ?? base.pctMessage,
    report: nextReport,
  };
}

/* ── Stream/Progress state (kept for backward compat) ────────────── */

// Legacy compat — no longer used but kept so external imports don't break
export interface StreamState {
  chatId: string | null;
  streamId: string | null;
  messageId: string | null;
}
export interface ProgressState {
  messageId: string | null;
  data: ReportProgressData | undefined;
}
export function createStreamState(): StreamState {
  return { chatId: null, streamId: null, messageId: null };
}
export function createProgressState(): ProgressState {
  return { messageId: null, data: undefined };
}

/* ── Handler context ─────────────────────────────────────────────── */

export interface HandlerContext {
  chatId: string;
  builder: AssistantBuilderState;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setToolUse: React.Dispatch<React.SetStateAction<string | null>>;
  onUnauthorized?: () => void;
  onHistoryLoaded?: (hasMore: boolean, nextBefore: string | null) => void;
}

/* ── Handlers ────────────────────────────────────────────────────── */

function handleConnected(_event: WsIncomingEvent, _ctx: HandlerContext): void {
  // Informational — socket.onopen already set status.
}

function handleHistory(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "history" || !Array.isArray(event.messages)) return;
  const normalized = normalizeHistoryMessages(event.messages);
  ctx.setMessages((prev) => mergeMessagesById(prev, normalized));

  const hasMore = typeof event.hasMore === "boolean" ? event.hasMore : Boolean(event.nextBefore);
  const nextBefore = typeof event.nextBefore === "string" ? event.nextBefore : null;
  ctx.onHistoryLoaded?.(hasMore, nextBefore);
}

function handleUserMessage(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "user_message") return;
  const serverId = event.id;

  if (serverId) {
    // Echo: find the oldest optimistic client message still awaiting a server id
    ctx.setMessages((prev) => {
      const index = prev.findIndex(
        (msg) => msg.role === "user" && msg._optimisticId !== undefined
      );
      if (index === -1) return prev;
      const pending = prev[index];
      if (!pending) return prev;
      const next = [...prev];
      next[index] = {
        ...pending,
        id: serverId,
        _optimisticId: undefined,
      };
      return next;
    });
  }

  // Reset builder — all subsequent events until `result` build ONE assistant message
  resetBuilder(ctx.builder);
}

function handleQueueStatus(_event: WsIncomingEvent, _ctx: HandlerContext): void {
  // Ignored per spec
}

function handleReviewProgress(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "review_progress") return;
  const record = event as Record<string, unknown>;
  const pct = typeof record.progressPct === "number" ? record.progressPct : undefined;
  const step = readString(record.progressStep);
  const message = readString(record.progressMessage);

  ctx.builder.progressData = mergeProgressData(ctx.builder.progressData, { pct, step, message });
  upsertAssistantMessage(ctx.setMessages, ctx.builder, ctx.chatId);
}

function handleReviewJob(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "review_job") return;
  const job = toRecord((event as Record<string, unknown>).job);
  if (!job) return;

  const { builder } = ctx;

  // Merge job data into reviewData (later events may bring more complete data)
  builder.reviewData = { ...(builder.reviewData ?? {}), ...job };

  // Extract progress fields from the job
  const pct = typeof job.progressPct === "number" ? job.progressPct : undefined;
  const step = readString(job.progressStep);
  const message = readString(job.progressMessage);
  const report = toRecord(job.report);

  builder.progressData = mergeProgressData(builder.progressData, { pct, step, message, report });

  upsertAssistantMessage(ctx.setMessages, builder, ctx.chatId);
}

function handleReportReady(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "report_ready") return;
  const record = event as Record<string, unknown>;
  // Report info may be at the root OR nested under `report`
  const reportPayload = toRecord(record.report) ?? record;

  ctx.builder.progressData = mergeProgressData(ctx.builder.progressData, {
    pct: 100,
    report: { ...reportPayload, status: "ready" },
  });

  upsertAssistantMessage(ctx.setMessages, ctx.builder, ctx.chatId);
}

function handleAssistantDelta(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "assistant_delta") return;
  const delta = readString(event.delta) ?? extractEventText(event);
  if (!delta) return;

  const streamId = readString(event.streamId);
  const { builder } = ctx;

  // Lock onto the first stream id we see; later deltas with a mismatched
  // streamId are dropped (they don't belong to this turn).
  if (streamId && !builder.streamId) {
    builder.streamId = streamId;
  }
  if (streamId && builder.streamId && builder.streamId !== streamId) {
    return;
  }

  builder.deltaText += delta;
  upsertAssistantMessage(ctx.setMessages, builder, ctx.chatId);
}

function handleAssistantMessage(event: WsIncomingEvent, ctx: HandlerContext): void {
  const record = event as Record<string, unknown>;
  const { builder } = ctx;

  // Replace text content with the complete version if provided
  const text = toText(record.content ?? record.parts ?? record.message ?? record.text);
  if (text) {
    builder.deltaText = text;
  }

  // Merge metadata.structured fields (new schema) OR metadata directly (old)
  const metadata = toRecord(record.metadata);
  const structured = toRecord(metadata?.structured) ?? metadata;
  if (structured) {
    const review = toRecord(structured.review);
    if (review) {
      builder.reviewData = { ...(builder.reviewData ?? {}), ...review };
    }
    if (Array.isArray(structured.citations)) {
      builder.citations = structured.citations;
    }
    if (Array.isArray(structured.followUps)) {
      builder.followUps = extractSuggestedFollowUps(structured.followUps);
    }
  }

  upsertAssistantMessage(ctx.setMessages, builder, ctx.chatId);
}

function handleAssistantDone(_event: WsIncomingEvent, ctx: HandlerContext): void {
  ctx.builder.showStatus = true;
  upsertAssistantMessage(ctx.setMessages, ctx.builder, ctx.chatId);
}

function handleResult(_event: WsIncomingEvent, ctx: HandlerContext): void {
  const event = _event as Record<string, unknown>;
  const success = event.success === true;
  const resultRecord = toRecord(event.result);
  const reviewRecord = toRecord(resultRecord?.review) ?? toRecord(event.review);
  const reportRecord =
    toRecord(resultRecord?.report) ??
    toRecord(reviewRecord?.report) ??
    toRecord(event.report);

  if (reviewRecord) {
    ctx.builder.reviewData = { ...(ctx.builder.reviewData ?? {}), ...reviewRecord };
  }
  if (success && reportRecord) {
    ctx.builder.progressData = mergeProgressData(ctx.builder.progressData, {
      pct: 100,
      report: { ...reportRecord, status: "ready" },
    });
  }

  // Final event of a turn — flush then reset for the next turn
  ctx.builder.showStatus = true;
  upsertAssistantMessage(ctx.setMessages, ctx.builder, ctx.chatId);
  resetBuilder(ctx.builder);
}

function handleToolUse(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (event.type !== "tool_use") return;
  const toolName = readString(event.toolName);
  if (!toolName) return;
  ctx.setToolUse(toolName);
}

function handleError(event: WsIncomingEvent, ctx: HandlerContext): void {
  if (
    typeof (event as Record<string, unknown>).code === "string" &&
    ((event as Record<string, unknown>).code as string).toUpperCase() === "UNAUTHENTICATED"
  ) {
    ctx.onUnauthorized?.();
    return;
  }
  if (event.type === "unauthorized") {
    ctx.onUnauthorized?.();
    return;
  }
  const text = extractEventText(event) || "Something went wrong while processing the message.";
  ctx.setMessages((prev) => [...prev, createAssistantTextMessage(createMessageId("error"), text)]);
}

/* ── Handler map ─────────────────────────────────────────────────── */

type EventHandler = (event: WsIncomingEvent, ctx: HandlerContext) => void;

export const wsEventHandlers: Record<string, EventHandler> = {
  connected: handleConnected,
  history: handleHistory,
  user_message: handleUserMessage,
  queue_status: handleQueueStatus,
  review_progress: handleReviewProgress,
  review_job: handleReviewJob,
  report_ready: handleReportReady,
  assistant_delta: handleAssistantDelta,
  assistant_message: handleAssistantMessage,
  assistant_done: handleAssistantDone,
  result: handleResult,
  tool_use: handleToolUse,
  error: handleError,
  unauthorized: handleError,
};
